import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader, UserPlus } from "lucide-react";
import { useAuth, hasAccount } from "../lib/auth";
import { useDesign } from "../lib/store";
import {
  clearStartTogetherMessages,
  runStartTogether,
  START_TOGETHER_PROGRESS,
  useStartTogetherState,
} from "../lib/startTogetherController";
import {
  handOffToSystemBrowser,
  inAppBrowserHost,
  systemBrowserName,
} from "../lib/inAppBrowser";
import { cn } from "../lib/cn";

/**
 * Always-visible "Invite" in the top bar — the collaboration entry point
 * survives dismissing the onboarding guide and works signed out.
 *
 *   - Room already saved or shared (signed in) → the Share sheet.
 *   - Inside Instagram / Facebook / … → hand off to Safari or Chrome,
 *     since Google blocks sign-in there.
 *   - Otherwise → one-tap Start together (sign in, save, share, link).
 */
export function InviteButton({ onOpenShareSheet }: { onOpenShareSheet: () => void }) {
  const auth = useAuth();
  const activeSavedRoomId = useDesign((s) => s.activeSavedRoomId);
  const activeSharedRoomId = useDesign((s) => s.activeSharedRoomId);
  const st = useStartTogetherState();
  const [ownRun, setOwnRun] = useState(false);
  const [handoff, setHandoff] = useState<string | null>(null);

  const canOpenSheet =
    auth.status === "signed-in" && hasAccount(auth.user) && (activeSavedRoomId !== null || activeSharedRoomId !== null);

  // Only narrate runs started here; the coach bar narrates its own.
  const message = handoff ?? (ownRun ? (st.busyStep ? START_TOGETHER_PROGRESS[st.busyStep] : st.error ?? st.notice) : null);
  useEffect(() => {
    if (!message || st.busyStep) return;
    const t = setTimeout(() => {
      setHandoff(null);
      setOwnRun(false);
      clearStartTogetherMessages();
    }, 4000);
    return () => clearTimeout(t);
  }, [message, st.busyStep]);

  if (auth.status === "unavailable") return null;

  async function onClick() {
    setHandoff(null);
    if (canOpenSheet) {
      onOpenShareSheet();
      return;
    }
    if (inAppBrowserHost()) {
      const result = await handOffToSystemBrowser(window.location.href);
      if (result === "copied") {
        setHandoff(`Link copied — paste it in ${systemBrowserName()} to invite your partner.`);
      } else if (result === "failed") {
        setHandoff(`Open this page in ${systemBrowserName()} to invite your partner.`);
      }
      return;
    }
    setOwnRun(true);
    void runStartTogether();
  }

  const busy = st.busyStep !== null;
  const copied = ownRun && st.shared === "copy";

  return (
    <>
      <button
        type="button"
        onClick={() => void onClick()}
        disabled={busy}
        data-testid="invite-button"
        className="h-11 px-3 inline-flex items-center gap-1.5 rounded-xl text-body-sm font-semibold text-ink-900 bg-white ring-1 ring-ink-200 hover:bg-ink-50 active:bg-ink-100 transition-colors disabled:opacity-70"
      >
        {busy ? (
          <Loader className="size-4 animate-spin" aria-hidden />
        ) : copied ? (
          <Check className="size-4 text-emerald-600" aria-hidden />
        ) : (
          <UserPlus className="size-4" aria-hidden />
        )}
        {copied ? "Copied" : "Invite"}
      </button>
      <AnimatePresence>
        {message && (
          <motion.div
            role="status"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className={cn(
              "fixed right-3 left-3 md:left-auto md:w-80 top-[calc(var(--topbar-h)+0.5rem)] z-40 rounded-2xl px-4 py-3 text-body-sm leading-snug shadow-lg ring-1",
              ownRun && st.error
                ? "bg-rose-50 text-rose-800 ring-rose-200"
                : "bg-white text-ink-800 ring-ink-200"
            )}
          >
            {message}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
